<template>
  <v-slide-x-reverse-transition>
    <v-card
      class="elevation-5 config-details vop-bg-whisper"
      v-show="sideSheetVisible"
    >
      <div>
        <!-- Title -->
        <v-toolbar flat color="vop-bg-whisper">
          <v-toolbar-title class="subheading ttu font-weight-medium">
            <span v-if="isEditMode">
              {{ messages.label.EDIT_CONFIGURATION }}
            </span>
            <span v-else>{{ messages.label.ADD_CONFIGURATION }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="hide()">
            <v-icon medium>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-layout wrap>
            <!-- Loading indicator -->
            <v-flex xs12>
              <div
                v-if="
                  detailsLoading ||
                    addingKeyValueDetails ||
                    updatingKeyValueDetails
                "
              >
                <v-progress-linear
                  v-bind:indeterminate="true"
                ></v-progress-linear>
              </div>
            </v-flex>
            <v-flex xs12 v-if="configError.message !== ''">
              <v-alert :value="true" type="error">{{
                configError.message
              }}</v-alert>
            </v-flex>
            <v-flex xs10>
              <v-form>
                <!-- Group -->
                <v-flex xs11>
                  <v-combobox
                    :disabled="isEditMode"
                    v-model="selectedGroupLocal"
                    :label="messages.label.GROUP_NAME"
                    item-text="title"
                    item-value="title"
                    :items="groupsList"
                    clearable
                    data-vv-value-path="title"
                    data-vv-validate-on="change"
                    :error-messages="errors.collect('configGroup')"
                    data-vv-name="configGroup"
                    :counter="100"
                    placeholder="Select or Create Group"
                    v-validate="{
                      required: true,
                      max: 100,
                      regex: /^[0-9a-zA-Z_-]*$/i
                    }"
                  >
                    <template v-slot:no-data>
                      <v-list-tile>
                        <v-list-tile-title>
                          + Create new Group
                        </v-list-tile-title>
                      </v-list-tile>
                    </template>
                  </v-combobox>
                </v-flex>
                <!-- Key Name -->
                <v-flex xs11>
                  <v-text-field
                    v-model="keyTitle"
                    :disabled="isEditMode"
                    clearable
                    v-validate="{
                      required: true,
                      max: 100,
                      regex: /^[\.0-9A-Z_-]*$/i
                    }"
                    :error-messages="errors.collect('configKey')"
                    data-vv-name="configKey"
                    :counter="100"
                    :label="messages.label.CONFIG_KEY"
                    required
                  ></v-text-field>
                </v-flex>
                <!-- JSON Editor -->
                <v-flex xs12>
                  <v-layout class="indigo lighten-5 pt-2" row>
                    <v-flex xs12 offset-xs1>
                      <JSONEditor
                        v-bind:json="configDetailsValue"
                        v-on:update:json="configDetailsValueOut = $event"
                      />
                      <label
                        v-if="configDetailsValidationMsg"
                        class="pl-0 body-1 red--text"
                        >{{ configDetailsValidationMsg }}</label
                      >
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-form>
            </v-flex>
          </v-layout>
        </v-card-text>
      </div>
      <v-card-actions class="px-3">
        <v-spacer />
        <!-- Delete Config -->
        <v-dialog v-model="deleteDialog" width="500" v-if="isEditMode">
          <v-btn
            color="error"
            slot="activator"
            :disabled="configError.status === '404'"
            >{{ messages.label.DELETE }}
          </v-btn>
          <v-card>
            <v-card-title class="headline font-weight-light">{{
              messages.label.DELETE_CONFIG
            }}</v-card-title>
            <v-card-text>
              <p>
                {{ messages.info.DELETE_CONFIG_1 }}
                <b>{{ selectedKey.key }}</b> ?
              </p>
              <p v-html="messages.info.DELETE_CONFIG_2"></p>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="grey lighten-1 white--text"
                @click="deleteDialog = false"
                >{{ messages.label.CANCEL }}
              </v-btn>
              <v-btn
                color="error"
                :disabled="deletingKeyValueDetails || detailsLoading"
                :loading="deletingKeyValueDetails"
                @click="deleteKeyValueDetails()"
                >{{ messages.label.DELETE }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn
          color="primary"
          class="ml-2"
          :disabled="
            updatingKeyValueDetails ||
              detailsLoading ||
              configError.status === '404'
          "
          :loading="updatingKeyValueDetails"
          v-if="isEditMode"
          @click="updateKeyValueDetails"
          >{{ messages.label.SAVE }}
        </v-btn>
        <v-btn
          color="primary"
          class="ml-2"
          :disabled="addingKeyValueDetails || detailsLoading"
          :loading="addingKeyValueDetails"
          v-else
          @click="addKeyValueDetails"
          >{{ messages.label.ADD }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-slide-x-reverse-transition>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import * as utils from "@/Shared/utils";
import messages from "@/ConfigService/messages.json";
import * as configServiceDataService from "@/ConfigService/ConfigServiceDataService";
import { GET_GROUPS } from "@/store/constants/action-types";
import {
  SET_CONFIG_DETAILS_SIDE_SHEET,
  SET_SELECTED_KEY,
  SET_KEY_VALUE_DETAILS,
  SET_CONFIG_ERROR,
  SET_LOADING_GROUPS,
  SET_SELECTED_GROUP
} from "@/store/constants/mutation-types";
import JSONEditor from "./JSONEditor.vue";

/**
 * initialData
 */
function initialData(instance) {
  return {
    configDetailsValue: "",
    configDetailsValueOut: "",
    addingKeyValueDetails: false,
    updatingKeyValueDetails: false,
    deleteDialog: false,
    deletingKeyValueDetails: false,
    selectedGroupLocal: {},
    keyTitle: "",
    jsonString: "",
    configDetailsValidationMsg: "",
    dictionary: instance.getValidationDictionary()
  };
}

export default {
  name: "ConfigDetails",
  components: {
    JSONEditor
  },
  $_veeValidate: {
    validator: "new"
  },
  data: function() {
    return initialData(this);
  },
  computed: {
    ...mapState("configService", [
      "sideSheetVisible",
      "isEditMode",
      "groupsList",
      "selectedGroup",
      "selectedKey",
      "keyValueDetails",
      "detailsLoading",
      "keyValueDetailsRetrieved",
      "configError"
    ])
  },
  watch: {
    selectedKey: {
      handler(newVal /*, oldVal */) {
        this.keyTitle = newVal.key;
        this.selectedGroupLocal = this.selectedGroup;
      },
      deep: true
    },
    sideSheetVisible: {
      handler(newVal /*, oldVal */) {
        if (newVal === false) {
          this.SET_CONFIG_ERROR({
            status: "",
            message: ""
          });
        }
        this.$validator.reset();
      },
      deep: true
    },
    keyValueDetailsRetrieved: {
      handler(newVal /*, oldVal */) {
        if (newVal) this.setConfigDetailsValue();
      },
      deep: true
    },
    isEditMode: {
      handler(newVal /*, oldVal */) {
        if (newVal === false) this.resetConfigData();
      },
      deep: true
    }
  },
  methods: {
    ...mapMutations("configService", [
      SET_CONFIG_DETAILS_SIDE_SHEET,
      SET_SELECTED_KEY,
      SET_KEY_VALUE_DETAILS,
      SET_CONFIG_ERROR,
      SET_LOADING_GROUPS,
      SET_SELECTED_GROUP
    ]),
    ...mapActions("configService", [GET_GROUPS]),

    /**
     * reset component state
     */
    reset() {
      Object.assign(this.$data, initialData(this));
    },

    /**
     * Clean up data if required for create mode
     */
    resetConfigData() {
      this.reset();
      this.SET_SELECTED_KEY({});
      this.SET_KEY_VALUE_DETAILS({});
      this.$validator.reset();
    },

    /**
     * Hide side sheet view
     */
    hide() {
      this.SET_CONFIG_DETAILS_SIDE_SHEET(false);
      this.resetConfigData();
    },

    /**
     * Display json editor
     */
    setConfigDetailsValue() {
      this.configDetailsValue = this.keyValueDetails;
      this.configDetailsValueOut = this.configDetailsValue;
    },

    /**
     * Validation messages for vee-validate
     */
    getValidationDictionary() {
      return {
        custom: {
          configKey: {
            required: () => "Config Key can't be empty",
            max: "Config Key may not be greater than 100 characters",
            regex: "Valid characters are : A-Z a-z 0-9 _ - ."
          },
          configGroup: {
            required: () => "Config Group can't be empty",
            max: "Config Group may not be greater than 100 characters",
            regex: "Valid characters are : A-Z a-z 0-9 _ -"
          }
        }
      };
    },

    /**
     * Add new config object
     */
    addKeyValueDetails() {
      this.$validator.validateAll().then(result => {
        if (result) {
          let configType = "json";
          let configValue = "";
          if (utils.IsJson(this.configDetailsValueOut)) {
            configValue = JSON.parse(this.configDetailsValueOut);
          } else {
            configValue = this.$lodash.trim(this.configDetailsValueOut, '"');
            configValue = this.$lodash.toString(configValue);
            configType = "string";
          }
          // Validation for Config Details
          if (!this.validateConfigValue(configValue)) {
            return;
          }
          let key = this.keyTitle.trim();
          let group = this.selectedGroupLocal.trim();
          this.addingKeyValueDetails = true;
          return configServiceDataService
            .addKeyValuePair(group, key, configValue, configType)
            .then(response => {
              this.SET_CONFIG_ERROR({
                status: "",
                message: ""
              });
              this.reloadGroups(group);
              this.hide();
              return response;
            })
            .catch(error => {
              if (error.response.status == 409) {
                //TODO: Response doesn't come back in json structure (APIFIX)
                this.SET_CONFIG_ERROR({
                  status: "409",
                  message: error.response.data
                });
              } else {
                let response = error.response.data;
                if (response.isException) {
                  this.SET_CONFIG_ERROR({
                    status: "",
                    message: response.description
                  });
                }
              }
              return Promise.reject(error);
            })
            .finally(() => {
              this.addingKeyValueDetails = false;
            });
        }
      });
    },

    /**
     * Update Config details
     */
    updateKeyValueDetails() {
      this.$validator.validateAll().then(result => {
        if (result) {
          let configType = "json";
          let configValue;
          if (utils.IsJson(this.configDetailsValueOut)) {
            configValue = JSON.parse(this.configDetailsValueOut);
          } else {
            configValue = this.$lodash.trim(this.configDetailsValueOut, '"');
            configValue = this.$lodash.toString(configValue);
            configType = "string";
          }
          // Validation for Config Details
          if (!this.validateConfigValue(configValue)) {
            return;
          }
          let key = this.keyTitle.trim();
          let group = this.selectedGroupLocal.trim();
          this.updatingKeyValueDetails = true;
          return configServiceDataService
            .updateKeyValuePair(group, key, configValue, configType)
            .then(response => {
              this.SET_CONFIG_ERROR({
                status: "",
                message: ""
              });
              this.hide();
              return response;
            })
            .catch(error => {
              let response = error.response.data;
              if (response.isException) {
                this.SET_CONFIG_ERROR({
                  status: "",
                  message: response.description
                });
              }
              return Promise.reject(error);
            })
            .finally(() => {
              this.updatingKeyValueDetails = false;
            });
        }
      });
    },

    /**
     * Delete config details
     */
    deleteKeyValueDetails() {
      this.deletingKeyValueDetails = true;
      let key = this.keyTitle.trim();
      let group = this.selectedGroupLocal;
      this.updatingKeyValueDetails = true;
      return configServiceDataService
        .deleteKeyValuePair(group, key)
        .then(response => {
          this.SET_CONFIG_ERROR({
            status: "",
            message: ""
          });
          this.deleteKeyFromClient();
          this.reloadGroups(group);
          this.hide();
          return response;
        })
        .catch(error => {
          let response = error.response.data;
          if (response.isException) {
            this.SET_CONFIG_ERROR({
              status: "",
              message: response.description
            });
          }
          return Promise.reject(error);
        })
        .finally(() => {
          this.deleteDialog = false;
          this.deletingKeyValueDetails = false;
        });
    },

    /**
     * Reload groups and update selection
     */
    reloadGroups(currentGroup) {
      this.SET_LOADING_GROUPS(true);
      this.SET_SELECTED_GROUP("");
      this.GET_GROUPS().then((/* response */) => {
        this.SET_LOADING_GROUPS(false);
        let groupIndex = this.$lodash.indexOf(this.groupsList, currentGroup);
        if (groupIndex !== -1) {
          this.SET_SELECTED_GROUP(this.groupsList[groupIndex]);
        } else {
          this.SET_SELECTED_GROUP(this.groupsList[0]);
        }
      });
    },

    /**
     * Validates if config value is empty
     * @param {*} configValue
     */
    validateConfigValue(configValue) {
      let isValid = false;
      if (configValue === null || configValue === "") {
        this.configDetailsValidationMsg = "Config Details can't be empty";
      } else {
        this.configDetailsValidationMsg = "";
        isValid = true;
      }
      return isValid;
    },

    /**
     * Remove key entry when a key value pair is deleted from client
     *
     */
    deleteKeyFromClient(key) {}
  },
  mounted() {
    this.$validator.localize("en", this.dictionary);
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages;
    /**
     * Initial load
     */
  },
  beforeDestroy() {}
};
</script>

<style lang="scss">
.config-details {
  min-height: 40%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
</style>



// WEBPACK FOOTER //
// src/ConfigService/ConfigDetails.vue